/**
 * The prototype's time source.
 *
 * Deliberately a plain wall clock. The real runtime will drive the view from
 * `MusicClock` (Web Audio's `currentTime` minus output latency), but this phase
 * has no audio and no simulation, so "dummy time" is the whole input.
 *
 * `delta` is clamped so a backgrounded tab cannot produce a single enormous step
 * that jumps every animation forward at once.
 */
export class ViewClock {
  private origin = 0;
  private previous = 0;
  private started = false;

  /** Seconds since `start()`. */
  elapsed = 0;
  /** Seconds since the previous `tick()`, clamped to a sane frame. */
  delta = 0;
  /** Frames advanced since `start()`. */
  frame = 0;

  constructor(private readonly maxDelta = 1 / 15) {}

  start(now = performance.now()): void {
    this.origin = now;
    this.previous = now;
    this.elapsed = 0;
    this.delta = 0;
    this.frame = 0;
    this.started = true;
  }

  /** Advances the clock. Returns seconds elapsed since `start()`. */
  tick(now = performance.now()): number {
    if (!this.started) this.start(now);
    const seconds = (now - this.previous) / 1000;
    this.previous = now;
    this.delta = Math.min(Math.max(seconds, 0), this.maxDelta);
    this.elapsed = (now - this.origin) / 1000;
    this.frame += 1;
    return this.elapsed;
  }

  /**
   * Re-anchors after a pause so `elapsed` does not leap. Animation phase is
   * preserved by shifting the origin rather than resetting it.
   */
  resume(now = performance.now()): void {
    this.origin = now - this.elapsed * 1000;
    this.previous = now;
    this.delta = 0;
  }
}
